import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, Coffee, Moon, Users, Heart, AlertTriangle, Search, Filter, Bookmark} from 'lucide-react'
import { useArticles } from '@/hooks/useArticles'
import { useSavedArticles } from '@/hooks/useSavedArticles'
import { useAuth } from '@/hooks/useAuth'

const iconMap = {
  Users,
  Coffee,
  Moon,
  Heart,
  AlertTriangle
}

export default function ResourcesPage() {
  const { articles, total, loading, hasMore, fetchArticles } = useArticles()
  const { isAuthenticated } = useAuth()
  const { isSaved, toggleSave } = useSavedArticles()
  const [expandedArticle, setExpandedArticle] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [page, setPage] = useState(1)
  const ARTICLES_PER_PAGE = 5

  const categories = [
    { value: 'all', label: 'All Topics' },
    { value: 'separation-anxiety', label: 'Separation Anxiety' },
    { value: 'caffeine', label: 'Caffeine & Anxiety' },
    { value: 'nocturnal-panic', label: 'Nocturnal Panic' },
    { value: 'gad-treatment', label: 'GAD Treatment' },
    { value: 'general', label: 'General' }
  ]

  useEffect(() => {
    const filter = selectedCategory !== 'all' ? { category: selectedCategory } : {}
    setPage(1)
    fetchArticles({ filter, search: searchTerm || undefined, limit: ARTICLES_PER_PAGE, skip: 0 })
  }, [searchTerm, selectedCategory])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const filter = selectedCategory !== 'all' ? { category: selectedCategory } : {}
    setPage(1)
    fetchArticles({ filter, search: searchTerm || undefined, limit: ARTICLES_PER_PAGE, skip: 0 })
  }

  const handleLoadMore = () => {
    const filter = selectedCategory !== 'all' ? { category: selectedCategory } : {}
    const nextPage = page + 1
    setPage(nextPage)
    fetchArticles({ 
      filter, 
      search: searchTerm || undefined, 
      limit: ARTICLES_PER_PAGE, 
      skip: (nextPage - 1) * ARTICLES_PER_PAGE,
      append: true 
    })
  }

  const staticArticles = [
    {
      icon: Users,
      title: 'Separation Anxiety in Adults and Children',
      excerpt: 'Understanding how separation anxiety manifests differently across age groups and effective coping strategies.',
      content: `Separation anxiety isn't just a childhood condition. While commonly associated with young children who fear being away from parents, separation anxiety can persist into adulthood or emerge later in life.

**In Children:**
Separation anxiety disorder affects approximately 4% of children in the United States. Symptoms include excessive distress when separated from caregivers, nightmares about separation, and physical complaints like headaches or stomachaches when separation is anticipated.

**In Adults:**
Adult separation anxiety often goes undiagnosed but can significantly impact relationships and daily functioning. Adults may experience intense worry about harm coming to loved ones, reluctance to be alone, or need for constant contact with partners or family members.

**Treatment Approaches:**
- Cognitive Behavioral Therapy helps identify and challenge catastrophic thinking
- Gradual exposure to separation situations builds tolerance
- Family therapy can address relationship dynamics
- Relaxation techniques reduce physical anxiety symptoms

The key difference in treatment between children and adults is developmental appropriateness—children benefit from parent involvement and play-based interventions, while adults focus on independence and relationship boundaries.`,
      color: 'text-[#4A90E2]',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Coffee,
      title: 'Caffeine and Anxiety: The Coffee Connection',
      excerpt: 'How your morning coffee might be intensifying anxiety symptoms and what you can do about it.',
      content: `If you struggle with anxiety, your daily coffee habit might be making things worse. Caffeine is a central nervous system stimulant that can trigger or worsen anxiety symptoms in susceptible individuals.

**The Caffeine-Anxiety Link:**
Caffeine consumption increases heart rate, can cause jitteriness, and mimics physical anxiety symptoms. For people with anxiety disorders, these sensations can trigger panic attacks or increase baseline worry levels.

**How Much is Too Much?**
- The average American consumes 200mg of caffeine daily (about 2 cups of coffee)
- Sensitive individuals may experience anxiety with as little as 100mg
- Tolerance varies widely—some people metabolize caffeine slowly (CYP1A2 gene)

**Signs Caffeine is Worsening Your Anxiety:**
- Jitteriness or trembling after coffee
- Racing heart or palpitations
- Difficulty concentrating
- Increased worry or restlessness
- Sleep disturbances that worsen daytime anxiety

**Management Strategies:**
- Gradually reduce intake (avoid cold turkey to prevent withdrawal headaches)
- Switch to half-caff or decaf alternatives
- Avoid caffeine after 2 PM for better sleep
- Monitor your intake from all sources (coffee, tea, energy drinks, chocolate)
- Consider L-theanine supplements (found in green tea) which may reduce caffeine's anxiogenic effects

**The Bottom Line:**
You don't necessarily need to eliminate caffeine entirely. Experiment with reduction and timing to find your personal threshold. Many people with anxiety find that cutting intake in half significantly reduces symptoms.`,
      color: 'text-[#48C9B0]',
      bgColor: 'bg-teal-50'
    },
    {
      icon: Moon,
      title: 'Nocturnal Panic Attacks: Why You Wake Up Anxious',
      excerpt: 'Understanding nighttime panic attacks and strategies to sleep peacefully through the night.',
      content: `Waking up in the middle of the night with your heart racing, sweating, and feeling intense fear is one of the most distressing anxiety experiences. Nocturnal panic attacks affect 18-45% of people with panic disorder.

**What Are Nocturnal Panic Attacks?**
Nocturnal panic attacks occur during sleep, typically during the transition between sleep stages (not during REM dreams). You wake abruptly with the same symptoms as daytime panic attacks: rapid heartbeat, chest tightness, shortness of breath, and overwhelming fear.

**Why They Happen:**
- Sleep-stage transitions can trigger the body's alarm system in sensitive individuals
- Increased sensitivity to internal sensations (like changes in breathing or heart rate)
- Not caused by dreams or nightmares (they're physiological, not psychological in origin)
- Often associated with daytime panic disorder or high stress levels

**Impact on Sleep and Anxiety:**
Nocturnal panic attacks create a vicious cycle—fear of having another attack causes sleep anxiety, which increases overall stress and makes attacks more likely. Many people develop insomnia as they try to avoid sleep.

**Treatment and Management:**
1. **Cognitive Behavioral Therapy for Insomnia (CBT-I):** Addresses sleep anxiety and unhelpful sleep behaviors
2. **Panic-Focused CBT:** Reduces overall panic frequency, including nighttime episodes
3. **Sleep Hygiene:** Consistent sleep schedule, cool bedroom, no caffeine after noon
4. **Relaxation Training:** Progressive muscle relaxation before bed calms the nervous system
5. **Medication:** SSRIs can reduce panic attack frequency; benzodiazepines for short-term relief (use cautiously)

**If You Have a Nocturnal Panic Attack:**
- Remind yourself it's a panic attack, not a medical emergency
- Use slow, deep breathing (4 counts in, 6 counts out)
- Get out of bed and do a calming activity until symptoms pass
- Avoid associating your bed with panic

**When to Seek Help:**
If nocturnal panic attacks occur frequently or severely impact your sleep quality, consult a mental health professional. Treatment is highly effective, and most people see substantial improvement within weeks.`,
      color: 'text-[#5DADE2]',
      bgColor: 'bg-sky-50'
    },
    {
      icon: AlertTriangle,
      title: 'Caffeine and Anxiety: Does Coffee Trigger Panic Attacks?',
      excerpt: 'Scientific evidence on the link between caffeine consumption and panic attacks, plus practical strategies.',
      content: `For many Americans, coffee is a non-negotiable part of their morning routine. But if you experience panic attacks, that daily cup might be doing more harm than good. Understanding the caffeine-panic connection can be crucial for managing your anxiety.

**The Scientific Link Between Caffeine and Panic Attacks:**

Research consistently shows that caffeine can trigger panic attacks in susceptible individuals. A landmark study found that 71% of panic disorder patients experienced panic attacks after consuming 480mg of caffeine (about 4-5 cups of coffee), compared to only 5% of healthy controls.

**How Caffeine Triggers Panic:**

Caffeine works by blocking adenosine receptors in your brain, which increases alertness but also activates your sympathetic nervous system—your "fight or flight" response. This causes:
- Increased heart rate and palpitations
- Elevated blood pressure
- Increased breathing rate
- Release of adrenaline and cortisol (stress hormones)
- Heightened sensory perception

For someone prone to panic attacks, these physical sensations can be misinterpreted as danger signals, triggering a full panic response.

**Individual Sensitivity Varies:**

Not everyone reacts to caffeine the same way. Your sensitivity depends on:
- **Genetics:** CYP1A2 gene variations affect how quickly you metabolize caffeine (slow metabolizers are more sensitive)
- **Anxiety disorder type:** People with panic disorder are most vulnerable
- **Tolerance:** Regular caffeine users develop partial tolerance, but anxiety-triggering effects may persist
- **Amount consumed:** Even moderate amounts (200mg) can trigger panic in sensitive individuals

**Caffeine Sources Beyond Coffee:**

Be aware of hidden caffeine sources that can add up:
- Espresso: 63mg per shot
- Brewed coffee: 95-200mg per 8oz cup
- Energy drinks: 80-300mg per can
- Black tea: 47mg per cup
- Green tea: 28mg per cup
- Dark chocolate: 12mg per ounce
- Some medications (Excedrin, Midol): 65mg per dose
- Pre-workout supplements: 150-300mg per serving

**The Withdrawal Factor:**

If you're a regular coffee drinker, suddenly quitting can also trigger anxiety and panic-like symptoms:
- Headaches and fatigue (days 1-2)
- Increased irritability and anxiety (days 2-4)
- Difficulty concentrating
- Depressed mood

This is why gradual reduction is recommended over cold turkey.

**Signs Caffeine is Triggering Your Panic Attacks:**
- Panic attacks occur within 1-2 hours of caffeine consumption
- Physical symptoms (racing heart, trembling) start before psychological panic
- Attacks are more frequent on high-caffeine days
- Symptoms improve when you reduce intake
- Morning anxiety peaks correlate with coffee consumption

**Evidence-Based Management Strategies:**

**1. Gradual Reduction Protocol:**
- Week 1: Reduce by 25% (mix regular with decaf)
- Week 2: Reduce by 50% (half-caff)
- Week 3: Reduce by 75% (mostly decaf)
- Week 4: Decaf only or caffeine-free alternatives

**2. Strategic Timing:**
- Avoid caffeine on empty stomach
- No caffeine after 12 PM (improves sleep, which reduces anxiety)
- Never consume caffeine when already feeling anxious

**3. Alternative Beverages:**
- Decaf coffee (2-5mg caffeine)
- Herbal teas (chamomile, peppermint, rooibos)
- Golden milk (turmeric latte)
- Chicory coffee (caffeine-free coffee substitute)

**4. L-Theanine Buffer:**
If you can't quit entirely, L-theanine (an amino acid in green tea) reduces caffeine's anxiety-inducing effects. Consider 200mg L-theanine supplement with your coffee.

**Clinical Perspective:**

Most anxiety specialists recommend that patients with panic disorder eliminate or drastically reduce caffeine. A study in the Journal of Clinical Psychiatry found that 60% of panic disorder patients who eliminated caffeine reported significant symptom reduction.

**What About Decaf?**

Decaf isn't completely caffeine-free (2-15mg per cup) but rarely triggers panic attacks. However, some people are sensitive to other coffee compounds (chlorogenic acids) that can still cause jitteriness.

**When to Seek Professional Help:**

If you experience frequent panic attacks even after eliminating caffeine, consult a mental health professional. Panic disorder requires treatment—Cognitive Behavioral Therapy (CBT) and/or medication—beyond lifestyle changes.

**The Bottom Line:**

While caffeine doesn't cause panic disorder, it's a powerful trigger for those already susceptible. If you experience panic attacks, eliminating caffeine is one of the most evidence-based self-help strategies you can implement. Most people see improvement within 2-4 weeks of cessation.

Your morning ritual doesn't have to include caffeine to be enjoyable—and the reduction in panic attack frequency is worth the transition for most people.`,
      color: 'text-[#4A90E2]',
      bgColor: 'bg-amber-50'
    },
    {
      icon: Heart,
      title: 'Generalized Anxiety Disorder Treatment Options',
      excerpt: 'Comprehensive guide to evidence-based treatments for managing chronic worry and GAD symptoms.',
      content: `Generalized Anxiety Disorder (GAD) involves persistent, excessive worry about various aspects of life—work, health, family, finances—that's difficult to control. Fortunately, multiple evidence-based treatment options can provide significant relief.

**First-Line Treatments:**

**1. Cognitive Behavioral Therapy (CBT)**
The gold-standard psychological treatment for GAD. CBT for GAD specifically targets:
- Worry postponement techniques
- Cognitive restructuring of "what if" thoughts
- Relaxation training and applied relaxation
- Problem-solving skills for realistic concerns

Success rate: 50-60% of patients achieve remission with CBT alone.

**2. Medication Management**
Several medication classes effectively treat GAD:
- **SSRIs** (Selective Serotonin Reuptake Inhibitors): Lexapro, Zoloft, Prozac
- **SNRIs** (Serotonin-Norepinephrine Reuptake Inhibitors): Effexor, Cymbalta
- **Buspirone:** Non-addictive anti-anxiety medication
- **Benzodiazepines:** Short-term use only due to dependence risk

**Second-Line and Complementary Approaches:**

**3. Acceptance and Commitment Therapy (ACT)**
Teaches accepting worry as a normal mental event while committing to valued actions despite anxiety.

**4. Mindfulness-Based Stress Reduction (MBSR)**
8-week program teaching mindfulness meditation to reduce anxiety reactivity.

**5. Applied Relaxation**
Systematic training in progressive muscle relaxation and rapid relaxation techniques for everyday use.

**Lifestyle Modifications:**
- Regular aerobic exercise (30 minutes, 5x/week) reduces GAD symptoms significantly
- Limiting caffeine and alcohol
- Consistent sleep schedule
- Structured worry time (15 minutes daily to address concerns)

**Combination Treatment:**
Research shows combining CBT with medication often produces the best outcomes, especially for moderate-to-severe GAD. Many patients start with both and then taper medication while maintaining therapy skills.

**Treatment Timeline:**
- Medication: 4-6 weeks to see full effects
- CBT: 12-16 sessions show substantial improvement
- Maintenance: Ongoing practice of skills prevents relapse

**Finding Treatment:**
Most Americans with GAD respond well to treatment but many don't seek help due to stigma or lack of awareness. If you've been struggling with persistent worry, consulting a mental health professional is the first step toward relief.`,
      color: 'text-[#48C9B0]',
      bgColor: 'bg-emerald-50'
    }
  ]

  const displayArticles = articles.length > 0 ? articles : staticArticles

  if (loading && articles.length === 0) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A90E2] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#48C9B0] to-[#5DADE2] text-white py-20" aria-labelledby="resources-heading">
        <div className="absolute inset-0 bg-black opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 id="resources-heading" className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Anxiety Resources and Insights
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Evidence-based information on specific anxiety situations and treatment approaches.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6" role="search" aria-label="Search articles">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search articles by topic, keyword, or symptom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-lg border-2 border-white border-opacity-30 bg-white bg-opacity-20 backdrop-blur-sm text-white placeholder-blue-100 focus:outline-none focus:border-white focus:bg-opacity-30 transition-all"
                  aria-label="Search articles by topic, keyword, or symptom"
                />
              </div>
            </form>

            {/* Category Filters */}
            <div className="flex items-center gap-3 flex-wrap" role="group" aria-label="Filter articles by category">
              <Filter className="w-5 h-5 text-blue-100" aria-hidden="true" />
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === cat.value
                      ? 'bg-white text-[#4A90E2] shadow-lg'
                      : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                  }`}
                  aria-label={`Filter by ${cat.label}`}
                  aria-pressed={selectedCategory === cat.value}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16" aria-label="Article list">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Summary */}
          <div className="mb-8 text-center" role="status" aria-live="polite">
            <p className="text-gray-600">
              {loading && articles.length === 0 ? 'Searching...' : `Showing ${displayArticles.length} of ${articles.length > 0 ? total : displayArticles.length} article${(articles.length > 0 ? total : displayArticles.length) !== 1 ? 's' : ''}`}
              {searchTerm && ` matching "${searchTerm}"`}
              {selectedCategory !== 'all' && ` in ${categories.find(c => c.value === selectedCategory)?.label}`}
            </p>
          </div>
          
          {displayArticles.length === 0 && !loading && (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No articles found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('all')
                }}
                className="px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#357ABD] transition-colors"
                aria-label="Clear all search filters"
              >
                Clear Filters
              </button>
            </div>
          )}
          
          <div className="space-y-8">
            {displayArticles.map((article, index) => {
              const Icon = iconMap[article.iconType as keyof typeof iconMap] || AlertTriangle
              const isExpanded = expandedArticle === index

              return (
                <article
                  key={article._id || index}
                  className={`bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all ${
                    isExpanded ? 'shadow-xl' : 'hover:shadow-lg'
                  }`}
                  aria-labelledby={`article-title-${index}`}
                >
                  <div className={`${article.bgColor} p-8`}>
                    <div className="flex items-start">
                      <div className={`w-12 h-12 ${article.bgColor} rounded-lg flex items-center justify-center mr-4 flex-shrink-0 border-2 ${article.color} border-opacity-20`} aria-hidden="true">
                        <Icon className={`w-6 h-6 ${article.color}`} />
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-start justify-between">
                          <div className="flex-grow pr-4">
                            <h2 id={`article-title-${index}`} className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                              {article.title}
                            </h2>
                          </div>
                          <button
                            onClick={() => toggleSave(article._id)}
                            className="p-2 rounded-lg bg-white hover:bg-gray-100 transition-colors flex-shrink-0"
                            title={isSaved(article._id) ? 'Remove from saved' : 'Save article'}
                            aria-label={isSaved(article._id) ? `Remove ${article.title} from saved articles` : `Save ${article.title} for later`}
                            aria-pressed={isSaved(article._id)}
                          >
                            <Bookmark className={`w-6 h-6 transition-colors ${
                              isSaved(article._id) ? 'text-[#4A90E2] fill-[#4A90E2]' : 'text-gray-400 hover:text-[#4A90E2]'
                            }`} aria-hidden="true" />
                          </button>
                        </div>
                        <p className="text-lg text-gray-700 mb-4">{article.excerpt}</p>
                        <button
                          onClick={() => setExpandedArticle(isExpanded ? null : index)}
                          className={`inline-flex items-center ${article.color} font-semibold hover:underline`}
                          aria-expanded={isExpanded}
                          aria-controls={`article-content-${index}`}
                          aria-label={isExpanded ? `Collapse article: ${article.title}` : `Expand article: ${article.title}`}
                        >
                          {isExpanded ? 'Show Less' : 'Read Full Article'}
                          <ArrowRight className={`ml-2 w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div id={`article-content-${index}`} className="p-8 bg-white" role="region" aria-label="Article content">
                      <div className="prose prose-lg max-w-none">
                        {article.content.split('\n\n').map((paragraph, idx) => {
                          if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                            return (
                              <h3 key={idx} className="text-xl font-bold text-gray-900 mt-6 mb-3">
                                {paragraph.replace(/\*\*/g, '')}
                              </h3>
                            )
                          } else if (paragraph.startsWith('- ')) {
                            const items = paragraph.split('\n- ').map(item => item.replace(/^- /, ''))
                            return (
                              <ul key={idx} className="list-disc list-inside space-y-2 mb-4 text-gray-700">
                                {items.map((item, i) => (
                                  <li key={i} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                                ))}
                              </ul>
                            )
                          } else {
                            return (
                              <p key={idx} className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ 
                                __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                              }} />
                            )
                          }
                        })}
                      </div>
                    </div>
                  )}
                </article>
              )
            })}
          </div>
          
          {/* Load More Button */}
          {articles.length > 0 && hasMore && (
            <div className="mt-12 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-8 py-4 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#357ABD] transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                aria-label="Load more articles"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Loading more...
                  </>
                ) : (
                  <>
                    Load More Articles
                    <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#4A90E2] to-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Take Action Today
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Knowledge is the first step—assessment and professional support are next
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg"
            >
              Take the GAD-7 Quiz
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/therapy"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#4A90E2] transition-all"
            >
              Find a Therapist Near You
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
